import { buildThemeWithLegacyOptions } from "@keepeek/refront-components";
import { CustomerAppTheme } from "@keepeek/refront-customers";
import { Theme, useTheme } from "@mui/material";
import { ThemeOptions } from "@mui/material/styles/createTheme";
import { deepmerge } from "@mui/utils";
import { AnyColor, Colord, colord, extend } from "colord";
import namesPlugin from "colord/plugins/names";
import * as CSS from "csstype";
import cloneDeep from "lodash/cloneDeep";
import { useRecoilValue } from "recoil";

import { CONFIGURATION_SECTION_PATH } from "../components/layout/adminMenu";
import { Fonts, Resource, Theme as AdminThemeConfiguration } from "../models/configuration/theme";
import { useConfiguration } from "../providers/config/hooks";
import { customerAppThemeSelector } from "../providers/config/selectors/init";
import { frontEditState } from "../providers/frontEdit/atoms/frontEdit";
import { getCurrentContentEditsSelector } from "../providers/frontEdit/selectors/currentContentEdits";
import logger from "./logger-utils";
import { useThemeTemplates } from "./theme-templates-utils";

// eslint-disable-next-line jest/require-hook
extend([namesPlugin]);
export const DEFAULT_FONT = "Inter";

export function isValidColor(color: AnyColor | Colord) {
  return colord(color)?.isValid();
}

export function toHexColor(color: AnyColor | Colord) {
  return colord(color)?.toHex();
}

const buildCustomerTheme = (
  customerThemeOverride: CustomerAppTheme | undefined,
  adminThemeConfiguration: AdminThemeConfiguration | undefined,
  defaultMuiTheme: Theme,
): Theme => {
  const {
    background,
    primary,
    secondary,
    error,
    text,
    borderRadius,
    fonts: fontsFromAdmin,
    extendThemeOptions,
  } = adminThemeConfiguration ?? {};

  const customerHasFontOverride =
    !!customerThemeOverride?.materialThemeOptions?.components?.MuiCssBaseline?.styleOverrides?.[
      "@font-face"
    ];

  const fonts = getFontsFromAdminOrDefault(customerHasFontOverride, fontsFromAdmin);

  const adminThemeOptions: ThemeOptions = {
    palette: {
      ...(background &&
        background.default &&
        isValidColor(background.default) && {
          background: {
            default: toHexColor(background.default),
            paper:
              background.paper && isValidColor(background.paper)
                ? toHexColor(background.paper)
                : undefined,
          },
        }),

      ...(primary &&
        primary.main &&
        isValidColor(primary.main) && {
          primary: {
            main: toHexColor(primary.main),
            light:
              primary.light && isValidColor(primary.light) ? toHexColor(primary.light) : undefined,
          },
        }),

      ...(secondary &&
        secondary.main &&
        isValidColor(secondary.main) && {
          secondary: {
            main: toHexColor(secondary.main),
          },
        }),

      ...(error &&
        error.main &&
        isValidColor(error.main) && {
          error: {
            main: toHexColor(error.main),
          },
        }),

      ...(text &&
        (text.primary || text.secondary || text.disabled || text.hint) && {
          text: {
            ...(text.primary &&
              isValidColor(text.primary) && {
                primary: toHexColor(text.primary),
              }),
            ...(text.secondary &&
              isValidColor(text.secondary) && {
                secondary: toHexColor(text.secondary),
              }),
            ...(text.disabled &&
              isValidColor(text.disabled) && {
                disabled: toHexColor(text.disabled),
              }),
            ...(text.hint &&
              isValidColor(text.hint) && {
                hint: toHexColor(text.hint),
              }),
          },
        }),
    },
    shape: {
      ...(borderRadius && { borderRadius }),
    },
    ...(fonts &&
      fonts.assignment && {
        typography: {
          ...(fonts.assignment.global && {
            ...defaultMuiTheme.typography,
            fontFamily: fonts.assignment.global,
          }),
          ...(fonts.assignment.h1 && {
            h1: {
              ...defaultMuiTheme.typography.h1,
              fontFamily: fonts.assignment.h1,
            },
          }),
          ...(fonts.assignment.h2 && {
            h2: {
              ...defaultMuiTheme.typography.h2,
              fontFamily: fonts.assignment.h2,
            },
          }),
          ...(fonts.assignment.h3 && {
            h3: {
              ...defaultMuiTheme.typography.h3,
              fontFamily: fonts.assignment.h3,
            },
          }),
          ...(fonts.assignment.h4 && {
            h4: {
              ...defaultMuiTheme.typography.h4,
              fontFamily: fonts.assignment.h4,
            },
          }),
          ...(fonts.assignment.h5 && {
            h5: {
              ...defaultMuiTheme.typography.h5,
              fontFamily: fonts.assignment.h5,
            },
          }),
          ...(fonts.assignment.h6 && {
            h6: {
              ...defaultMuiTheme.typography.h6,
              fontFamily: fonts.assignment.h6,
            },
          }),
          ...(fonts.assignment.global && {
            body1: {
              ...defaultMuiTheme.typography.body1,
              fontFamily: fonts.assignment.global,
            },
            body2: {
              ...defaultMuiTheme.typography.body2,
              fontFamily: fonts.assignment.global,
            },
            button: {
              ...defaultMuiTheme.typography.button,
              fontFamily: fonts.assignment.global,
            },
            caption: {
              ...defaultMuiTheme.typography.caption,
              fontFamily: fonts.assignment.global,
            },
            overline: {
              ...defaultMuiTheme.typography.overline,
              fontFamily: fonts.assignment.global,
            },
            subtitle1: {
              ...defaultMuiTheme.typography.subtitle1,
              fontFamily: fonts.assignment.global,
            },
            subtitle2: {
              ...defaultMuiTheme.typography.subtitle2,
              fontFamily: fonts.assignment.global,
            },
          }),
        },
      }),

    // historical overrides... i don't know why :
    components: {
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: ".25rem",
          },
        },
      },
    },
  };

  let themeOptions = deepmerge<ThemeOptions>(
    customerThemeOverride?.materialThemeOptions ?? {},
    adminThemeOptions,
  );

  // are there overloads of the theme from the admin front?
  if (extendThemeOptions) {
    try {
      themeOptions = deepmerge<ThemeOptions>(themeOptions, JSON.parse(extendThemeOptions));
    } catch (error) {
      logger.error(`buildCustomerTheme - extendThemeOptions is invalid to parse: ${error}`);
    }
  }
  try {
    return buildThemeWithLegacyOptions(themeOptions);
  } catch (error) {
    logger.error("buildCustomerTheme - extendThemeOptions is invalid", error);
  }
  return buildThemeWithLegacyOptions();
};

/**
 * Get the fonts to apply to the theme for the customer
 * If there is an admin configuration for the font we apply it
 * If there is an override, we apply the override
 * If there is no override and no admin configuration, we apply the default font
 *
 * @param customerHasFontOverride - Boolean to know if there is an override
 * @param adminFonts - The font configuration from the admin
 * @returns The fonts to apply to the theme.
 */
export const getFontsFromAdminOrDefault = (
  customerHasFontOverride: boolean,
  adminFonts?: Fonts,
): Fonts => {
  if (adminFonts && adminFonts.assignment) {
    return adminFonts;
  } else if (customerHasFontOverride) {
    return {};
  } else {
    return {
      assignment: {
        global: DEFAULT_FONT,
      },
    };
  }
};

/**
 * Hook allowing to return a theme, a theme without font and only the fonts
 */
export type UseCustomerTheme = {
  theme: Theme;
  themeWithoutCustomFonts: Theme;
  customFonts: CSS.AtRule.FontFace[] | undefined;
  defaultFonts: CSS.AtRule.FontFace[];
};
export const useCustomerTheme = (): UseCustomerTheme => {
  const customerThemeOverride = useRecoilValue(customerAppThemeSelector);
  let adminThemeConfiguration = useConfiguration<AdminThemeConfiguration>(
    CONFIGURATION_SECTION_PATH.THEME,
  );

  const frontEdit = useRecoilValue(frontEditState);
  const currentContentEdit = useRecoilValue(
    getCurrentContentEditsSelector({
      endpoint: `${CONFIGURATION_SECTION_PATH.THEME}`,
    }),
  );
  const currentEditData =
    currentContentEdit && currentContentEdit.data ? currentContentEdit.data : customerThemeOverride;

  /* Preview of theme parameters in edit mode */
  if (frontEdit && currentEditData) {
    // Values from admin
    const { background, primary, secondary, error, text, fonts, borderRadius } =
      adminThemeConfiguration || {};

    // Current edited values by front edit
    const { default: defaultBg, paper: paperBg } = currentEditData.background || {};
    const { main: primaryMain, light: primaryLight } = currentEditData.primary || {};
    const { main: secondaryMain } = currentEditData.secondary || {};
    const { main: errorMain } = currentEditData.error || {};
    const {
      primary: textPrimary,
      secondary: textSecondary,
      disabled: textDisabled,
      hint: textHint,
    } = currentEditData.text || {};
    const { assignment: fontAssignment } = currentEditData.fonts || {};
    const currentBorderRadius = currentEditData.borderRadius || borderRadius;

    adminThemeConfiguration = {
      ...adminThemeConfiguration,
      background: {
        ...background,
        default: isValidColor(defaultBg) ? toHexColor(defaultBg) : background?.default,
        paper: isValidColor(paperBg) ? toHexColor(paperBg) : background?.paper,
      },
      primary: {
        ...primary,
        main: isValidColor(primaryMain) ? toHexColor(primaryMain) : primary?.main,
        light: isValidColor(primaryLight) ? toHexColor(primaryLight) : primary?.light,
      },
      secondary: {
        ...secondary,
        main: isValidColor(secondaryMain) ? toHexColor(secondaryMain) : secondary?.main,
      },
      error: {
        ...error,
        main: isValidColor(errorMain) ? toHexColor(errorMain) : error?.main,
      },
      text: {
        ...text,
        primary: isValidColor(textPrimary) ? toHexColor(textPrimary) : text?.primary,
        secondary: isValidColor(textSecondary) ? toHexColor(textSecondary) : text?.secondary,
        disabled: isValidColor(textDisabled) ? toHexColor(textDisabled) : text?.disabled,
        hint: isValidColor(textHint) ? toHexColor(textHint) : text?.hint,
      },
      fonts: {
        ...fonts,
        assignment: fontAssignment || fonts?.assignment,
      },
      borderRadius: currentBorderRadius,
    };
  }
  const defaultMuiTheme = useTheme();

  const theme = buildCustomerTheme(customerThemeOverride, adminThemeConfiguration, defaultMuiTheme);
  const { themeTemplates } = useThemeTemplates(theme);
  const defaultFonts = [
    { fontFamily: DEFAULT_FONT },
    { fontFamily: "Arial" },
    { fontFamily: "Verdana" },
    { fontFamily: "Courier" },
    { fontFamily: "Georgia" },
    { fontFamily: "Trebuchet MS" },
  ];

  const themeWithTemplatesOverrides = deepmerge(theme, themeTemplates);

  const customFontsFromAdmin: CSS.AtRule.FontFace[] =
    adminThemeConfiguration?.fonts?.resources?.map((f: Resource) => {
      return {
        src: `url('${f.fontResource?.url}')`,
        fontFamily: f.name,
      };
    }) || [];

  let customFonts: CSS.AtRule.FontFace[] | undefined;
  const themeWithoutCustomFonts = cloneDeep(theme);
  if (theme.components?.MuiCssBaseline?.styleOverrides?.hasOwnProperty("@font-face")) {
    const tempFonts = theme?.components?.MuiCssBaseline?.styleOverrides?.["@font-face"];
    customFonts = Array.isArray(tempFonts) ? tempFonts : [tempFonts];
    delete themeWithoutCustomFonts?.components?.MuiCssBaseline?.styleOverrides?.["@font-face"];
  }
  return {
    theme: themeWithTemplatesOverrides,
    themeWithoutCustomFonts,
    customFonts:
      customFonts?.concat(customFontsFromAdmin).concat(defaultFonts) ||
      customFontsFromAdmin.concat(defaultFonts),
    defaultFonts,
  };
};
